import {
    useCallback,
    useMemo,
} from 'react';

import {
    useSearchParams,
} from 'react-router-dom';

import {
    useSelector,
} from 'react-redux';

import {
    normalizeCompanyID,
    selectAuthUser,
    selectCompanyID,
} from '../features/auth/authSlice';


/**
 * 给目标地址添加管理员 companyId 查询参数。
 *
 * 普通用户不会调用到这里。
 *
 * 示例：
 *
 * /motorcycles
 * =>
 * /motorcycles?companyId=0
 *
 * /motorcycles
 * =>
 * /motorcycles?companyId=f61733d2-...
 *
 * /motorcycles?page=2
 * =>
 * /motorcycles?page=2&companyId=f61733d2-...
 */
const appendCompanyQuery = (
    target,
    companyID,
) => {
    if (
        !target ||
        typeof target !== 'string'
    ) {
        return target;
    }

    const [
        pathAndQuery,
        hash = '',
    ] = target.split('#');

    const [
        pathname,
        query = '',
    ] = pathAndQuery.split('?');

    const params =
        new URLSearchParams(query);

    params.set(
        'companyId',
        String(companyID),
    );

    const queryString =
        params.toString();

    return `${pathname}${queryString
        ? `?${queryString}`
        : ''
        }${hash
            ? `#${hash}`
            : ''
        }`;
};


/**
 * 统一的 Company Context。
 *
 * Redux companyID 类型约定：
 *
 * null
 * - 尚未恢复
 * - 未登录
 *
 * 0
 * - ADMIN 查看 All companies
 *
 * UUID string
 * - 具体 company
 *
 *
 * 最终优先级：
 *
 * 普通用户：
 * Redux 固定 companyID
 *
 * ADMIN：
 * URL companyId
 * ↓
 * Redux companyID
 * ↓
 * 0
 */
export default function useCompanyContext() {
    const [searchParams] =
        useSearchParams();

    /**
     * 只订阅真正需要的 Redux 字段。
     *
     * 不订阅整个 state.auth，
     * 避免 accessToken / loading / error
     * 等无关字段变化时触发组件重新 render。
     */
    const user =
        useSelector(selectAuthUser);

    const reduxCompanyID =
        useSelector(selectCompanyID);

    const isAdmin =
        user?.role === 'ADMIN';


    /**
     * Redux 中保存的 companyID。
     *
     * 理论上经过 authSlice 后已经是：
     *
     * null | 0 | UUID
     *
     * 这里再 normalize 一次，
     * 是为了兼容旧的 sessionStorage 数据。
     */
    const storedCompanyID =
        useMemo(() => {
            const normalized =
                normalizeCompanyID(
                    reduxCompanyID,
                );

            if (
                normalized !== null
            ) {
                return normalized;
            }

            /**
             * ADMIN 没有有效值时：
             * 默认 All companies。
             *
             * 普通用户这里理论上不应该发生，
             * 因为 /users/me 会恢复其固定 companyID。
             */
            return isAdmin
                ? 0
                : null;
        }, [
            reduxCompanyID,
            isAdmin,
        ]);


    /**
     * URL 中原始 companyId。
     */
    const urlCompanyValue =
        searchParams.get(
            'companyId',
        );


    /**
     * URL companyId 只对 ADMIN 有效。
     *
     * 普通用户即使手动访问：
     *
     * /motorcycles?companyId=别人的UUID
     *
     * 也不会使用这个值。
     */
    const urlCompanyID =
        useMemo(() => {
            if (!isAdmin) {
                return null;
            }

            const normalized =
                normalizeCompanyID(
                    urlCompanyValue,
                );

            return normalized;
        }, [
            isAdmin,
            urlCompanyValue,
        ]);


    /**
     * 是否存在合法的管理员 URL company context。
     */
    const hasUrlCompanyContext =
        isAdmin &&
        urlCompanyID !== null;


    /**
     * 页面真正应该使用的 companyID。
     *
     * 普通用户：
     * 永远使用自己的固定 companyID。
     *
     * ADMIN：
     * URL 优先。
     *
     * 这保证了：
     * - 刷新
     * - 复制标签页
     * - 右键新标签页
     * - 多标签不同 company
     *
     * 都可以正确工作。
     */
    const currentCompanyID =
        useMemo(() => {
            if (!isAdmin) {
                return storedCompanyID;
            }

            if (
                hasUrlCompanyContext
            ) {
                return urlCompanyID;
            }

            return (
                storedCompanyID ??
                0
            );
        }, [
            isAdmin,
            storedCompanyID,
            hasUrlCompanyContext,
            urlCompanyID,
        ]);


    /**
     * ADMIN 当前正在查看全部公司。
     */
    const isAllCompanies =
        isAdmin &&
        currentCompanyID === 0;


    /**
     * 当前是否已经确定一个具体 company。
     *
     * 普通用户：
     * 正常情况下恒为 true。
     *
     * ADMIN：
     * companyID = 0 => false
     * UUID => true
     */
    const hasSpecificCompany =
        typeof currentCompanyID ===
        'string' &&
        currentCompanyID.length > 0;


    /**
     * 当前规则：
     *
     * 新建 company-owned resource
     * 必须已经确定具体 company。
     *
     * ADMIN All companies：
     * false
     *
     * ADMIN specific company：
     * true
     *
     * 普通供应商：
     * true
     */
    const canCreateCompanyResource =
        hasSpecificCompany;


    /**
     * 为业务导航地址附加 companyId。
     *
     * 普通用户：
     * 地址保持原样。
     *
     * ADMIN：
     * 自动附加 companyId。
     *
     * overrideCompanyID 可用于：
     * 管理员切换 company 时生成新的目标 URL。
     */
    const buildCompanyUrl =
        useCallback(
            (
                target,
                overrideCompanyID,
            ) => {
                if (!isAdmin) {
                    return target;
                }

                let nextCompanyID =
                    currentCompanyID;

                if (
                    overrideCompanyID !==
                    undefined
                ) {
                    const normalized =
                        normalizeCompanyID(
                            overrideCompanyID,
                        );

                    if (
                        normalized !== null
                    ) {
                        nextCompanyID =
                            normalized;
                    }
                }

                /**
                 * ADMIN 理论上永远至少是 0。
                 */
                if (
                    nextCompanyID ===
                    null ||
                    nextCompanyID ===
                    undefined
                ) {
                    nextCompanyID = 0;
                }

                return appendCompanyQuery(
                    target,
                    nextCompanyID,
                );
            },
            [
                isAdmin,
                currentCompanyID,
            ],
        );


    return {
        /**
         * 登录用户。
         */
        user,

        /**
         * 是否管理员。
         */
        isAdmin,

        /**
         * Redux 中保存的 company context。
         *
         * 注意：
         * 业务页面通常应该使用
         * currentCompanyID，
         * 而不是 storedCompanyID。
         */
        storedCompanyID,

        /**
         * URL 中解析出的 companyID。
         *
         * 普通用户恒为 null。
         */
        urlCompanyID,

        /**
         * 当前页面最终生效的 companyID。
         *
         * 这是业务页面最主要使用的字段。
         */
        currentCompanyID,

        /**
         * ADMIN 是否处于 All companies。
         */
        isAllCompanies,

        /**
         * 是否已经确定具体 company UUID。
         */
        hasSpecificCompany,

        /**
         * 当前是否可以创建
         * company-owned resource。
         */
        canCreateCompanyResource,

        /**
         * ADMIN 当前 URL 是否明确包含
         * 合法 company context。
         */
        hasUrlCompanyContext,

        /**
         * 创建保持 company context 的 URL。
         */
        buildCompanyUrl,
    };
}